import Link from "next/link";
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react";

import MaticLogo from "@assets/svg/MaticLogo";
import { useAnnounce } from "@/blueprint/components/LiveAnnouncer/useAnnounce";
import MobileNavCluster from "@/blueprint/components/NavPill/MobileNavCluster";
import NavPill from "@/blueprint/components/NavPill/NavPill";
import {
  CHROME_THEME_FADE_CLASSES,
  NavMotion,
  MOTION_EASE_IN,
  MOTION_EASE_OUT,
  MotionDuration,
  MotionTransition,
} from "@/blueprint/tokens/Motion";
import { getDisplayCartQuantity } from "@/cart/displayCart";
import useCart from "@/cart/useCart";
import { SHOP_HREF, SHOP_LABEL } from "@/chrome/chromeContent";
import DesktopNav from "@/chrome/SiteNav/DesktopNav";
import FloatingNavSurface from "@/chrome/SiteNav/FloatingNavSurface";
import NavMenuCard, { type NavMenuView } from "@/chrome/SiteNav/NavMenuCard";
import { NAV_WORDMARK_CLASSES } from "@/chrome/SiteNav/navWordmark";
import { useNavCollapse } from "@/chrome/SiteNav/useNavCollapse";
import { useNavHandoff } from "@/chrome/SiteNav/useNavHandoff";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useOnScreen } from "@/hooks/useOnScreen";
import { cn } from "@/lib/utils";
import useLayoutStore from "@/stores/useLayoutStore";
import { useTrack, type EventMap } from "@/analytics/events";

const NAV_OPEN_DELAY_MS = 120;
const DESKTOP_NAV_QUERY = "(min-width: 1280px)";
const MENU_CARD_ID = "site-nav-menu";

/** Which sub-desktop trigger the open sheet menu belongs to. */
type MenuSurface = "row" | "pill";
type MenuDismissMethod = EventMap["nav_dismissed"]["method"];

type NavControl = "cart" | "menu" | "shop";

type PendingFocusTransfer = {
  destination: "floating" | "in-flow";
  control: NavControl;
};

const getNavControl = (element: HTMLElement): NavControl => {
  const control = element
    .closest<HTMLElement>("[data-nav-control]")
    ?.getAttribute("data-nav-control");

  return control === "cart" || control === "shop" ? control : "menu";
};

const focusNavControl = (container: HTMLElement, control: NavControl) => {
  const matchingControls = Array.from(
    container.querySelectorAll<HTMLElement>(`[data-nav-control="${control}"]`),
  );
  const fallbackControls = Array.from(
    container.querySelectorAll<HTMLElement>(
      "[data-nav-control='menu'], a[href], button:not([disabled])",
    ),
  );
  const target = [...matchingControls, ...fallbackControls].find(
    (element) =>
      !element.closest("[inert]") && element.getClientRects().length > 0,
  );

  target?.focus();
};

type SiteNavProps = {
  topNavTheme?: "dark" | undefined;
  mode?: "default" | "collapsed";
  // Immersive pages (Life with Matic) hide the collapsed pill once scrolling
  // stops so the chrome stays out of the way; it reappears on the next scroll.
  /**
   * No floating pill: the nav scrolls away with the page (/product, whose
   * visual zone takes the top of the screen once it's gone).
   */
  scrollsAway?: boolean;
  /**
   * The id of an element the pill keeps out of the way of: it stays hidden
   * while that element is on screen and floats as usual once it has left
   * (the /lp pages' configurator, whose payment tabs take the top).
   */
  hiddenOverId?: string;
};

/**
 * How far into the handoff the pill starts accepting input. Short of 1 on
 * purpose: the state change costs a React commit, so gating on the full value
 * still left one frame where the pill was painted opaque and dead. Here it is
 * plainly visible already, and the row it is taking over from is still live
 * too, so nothing is unreachable in either direction.
 */
const HANDED_OFF_AT = 0.9;

const SiteNav = ({
  topNavTheme,
  mode = "default",
  scrollsAway = false,
  hiddenOverId,
}: SiteNavProps) => {
  const navRef = useRef<HTMLElement>(null);
  const floatingNavRef = useRef<HTMLDivElement>(null);
  const desktopFloatingMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileFloatingMenuButtonRef = useRef<HTMLButtonElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const pendingFocusTransferRef = useRef<PendingFocusTransfer | null>(null);
  const captureFocusBeforeCollapseChange = useCallback(
    (willCollapse: boolean) => {
      const source = willCollapse ? navRef.current : floatingNavRef.current;
      const activeElement = document.activeElement;

      if (
        source !== null &&
        activeElement instanceof HTMLElement &&
        source.contains(activeElement)
      ) {
        pendingFocusTransferRef.current = {
          destination: willCollapse ? "floating" : "in-flow",
          control: getNavControl(activeElement),
        };
      }
    },
    [],
  );
  const scrollCollapsed = useNavCollapse(
    navRef,
    captureFocusBeforeCollapseChange,
  );
  const collapsed = mode === "collapsed" || scrollCollapsed;
  const [menuSurface, setMenuSurface] = useState<MenuSurface | null>(null);
  const [menuView, setMenuView] = useState<NavMenuView>("menu");
  // Where the card sits through its close fade, after menuSurface is cleared.
  const lastSurfaceRef = useRef<MenuSurface>("row");
  const previousViewRef = useRef<NavMenuView>("menu");
  const rowMenuButtonRef = useRef<HTMLButtonElement>(null);
  const rowShopButtonRef = useRef<HTMLButtonElement>(null);
  const pillShopButtonRef = useRef<HTMLButtonElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const track = useTrack();
  const announce = useAnnounce();
  const [expanded, setExpanded] = useState(false);
  const cart = useCart((state) => state.cart);
  const openSideCart = useLayoutStore((state) => state.openSideCart);
  const cartCount = getDisplayCartQuantity(cart);
  const reducedMotion = useReducedMotion() === true;
  const rowOpen = menuSurface === "row";
  const pillOpen = menuSurface === "pill";
  // The trigger cluster IS the open menu's header, so the pill stays visible
  // while its own menu is open; it only yields to a row-opened one.
  // Position, not a timer: see useNavHandoff. `mode === "collapsed"` pages
  // show the pill with no row to hand over from, so they pin it at 1.
  const handoff = useNavHandoff(navRef, {
    forced: mode === "collapsed",
    reducedMotion,
  });
  /*
   * Whether the pill is fully painted, which is what decides that it can be
   * used — not `collapsed`.
   *
   * The handoff deliberately completes `leadPx` before the observer flips
   * `collapsed`, so keying interactivity off `collapsed` left the pill sitting
   * fully opaque and completely dead for that distance, every pass, in both
   * directions. It looks like the nav and does nothing.
   */
  const [handedOff, setHandedOff] = useState(handoff.get() >= HANDED_OFF_AT);
  useMotionValueEvent(handoff, "change", (value) =>
    setHandedOff(value >= HANDED_OFF_AT),
  );
  // Read from the page on every render, since the element is the page's,
  // not the nav's; declared before useOnScreen so it reads the fresh node.
  const hiddenOverRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    hiddenOverRef.current =
      hiddenOverId === undefined ? null : document.getElementById(hiddenOverId);
  });
  const overHidden = useOnScreen(hiddenOverRef, {
    enabled: hiddenOverId !== undefined,
  });
  const floatingVisible = (collapsed || handedOff) && !rowOpen && !overHidden;
  const handoffOpacity = useTransform(handoff, [0, 1], [0, 1]);
  const handoffLift = useTransform(
    handoff,
    [0, 1],
    [NavMotion.handoff.liftPx, 0],
  );
  const chromeFadeStyle = {
    transitionDuration: `${reducedMotion ? 0 : MotionDuration.quickMs}ms`,
    transitionTimingFunction: collapsed ? MOTION_EASE_OUT : MOTION_EASE_IN,
  };
  useEffect(() => {
    if (!collapsed) setExpanded(false);
  }, [collapsed]);

  // The sheet menu belongs to the sub-desktop compositions; resizing across
  // the desktop breakpoint while it is open would otherwise strand it.
  const desktopNav = useMediaQuery(DESKTOP_NAV_QUERY) === true;
  useEffect(() => {
    if (desktopNav) setMenuSurface(null);
  }, [desktopNav]);

  useLayoutEffect(() => {
    const pendingTransfer = pendingFocusTransferRef.current;
    const destination = collapsed ? "floating" : "in-flow";
    if (pendingTransfer?.destination !== destination) return;

    const container = collapsed ? floatingNavRef.current : navRef.current;
    if (container !== null) {
      focusNavControl(container, pendingTransfer.control);
    }
    pendingFocusTransferRef.current = null;
  }, [collapsed]);
  // The homepage draws its logo in on load; every other page shows it still.

  const collapseFloatingNav = () => {
    setExpanded(false);
    window.requestAnimationFrame(() =>
      desktopFloatingMenuButtonRef.current?.focus(),
    );
  };

  const openMenu = (surface: MenuSurface, view: NavMenuView = "menu") => {
    lastSurfaceRef.current = surface;
    setMenuView(view);
    setMenuSurface(surface);
    /* `panel` is omitted when the sheet opens on its own menu view: reporting
       "resources" there made every mobile open look like a resources-panel
       open, and the sheet's menu view is not a panel. */
    track(
      "nav_opened",
      view === "shop"
        ? { surface: "menu_sheet", panel: "shop" }
        : { surface: "menu_sheet" },
    );
  };

  // Switching an already-open sheet into its shop view is its own open.
  const openShopView = () => {
    setMenuView("shop");
    track("nav_opened", { surface: "menu_sheet", panel: "shop" });
  };

  const closeMenu = useCallback(
    (method: MenuDismissMethod, focusTrigger = false) => {
      if (menuSurface === null) return;
      track("nav_dismissed", { method });
      setMenuSurface(null);
      if (focusTrigger) {
        const trigger =
          menuSurface === "pill"
            ? mobileFloatingMenuButtonRef
            : rowMenuButtonRef;
        trigger.current?.focus();
      }
    },
    [menuSurface, track],
  );

  // A menu whose trigger is on its way out has nothing to hang off — closing
  // beats stranding an orphaned card, in either scroll direction.
  const previousCollapsedRef = useRef(collapsed);
  useEffect(() => {
    if (previousCollapsedRef.current === collapsed) return;
    previousCollapsedRef.current = collapsed;
    closeMenu("scroll");
  }, [collapsed, closeMenu]);

  useEffect(() => {
    if (menuSurface === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      closeMenu("escape", true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuSurface, closeMenu]);

  useEffect(() => {
    const previousView = previousViewRef.current;
    if (menuView === previousView) return;

    previousViewRef.current = menuView;
    if (menuView === "shop") {
      backButtonRef.current?.focus();
      announce("Shop menu");
    } else {
      const shopButton =
        menuSurface === "pill" ? pillShopButtonRef : rowShopButtonRef;
      shopButton.current?.focus();
      announce("Main navigation menu");
    }
  }, [announce, menuView, menuSurface]);

  const handleCartClick = () => {
    closeMenu("close_button");
    openSideCart();
  };

  const handleFloatingSurfaceMenuClick = () => {
    if (expanded) {
      collapseFloatingNav();
      return;
    }

    setExpanded(true);
    window.requestAnimationFrame(() => {
      const container = floatingNavRef.current;
      if (container !== null) focusNavControl(container, "menu");
    });
  };

  const cancelOpen = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  useEffect(() => cancelOpen, []);

  // Hovering the collapsed pill on desktop reveals the full floating nav, the
  // same hover-to-open the resting nav has. Intent is read from pointer
  // MOVEMENT, not pointerenter: the fixed pill regularly appears underneath a
  // parked cursor (scroll collapse, idle-hide reappearing), and enter fires
  // then without the user touching the mouse. Leaving before the delay cancels
  // the open; once expanded, the floating nav collapses itself on dismiss.
  const handleFloatingPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (
      expanded ||
      openTimerRef.current !== null ||
      event.pointerType !== "mouse" ||
      !window.matchMedia(DESKTOP_NAV_QUERY).matches
    ) {
      return;
    }
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      setExpanded(true);
    }, NAV_OPEN_DELAY_MS);
  };

  const handleFloatingPointerLeave = () => cancelOpen();

  // The shop sub-view keeps only the close control on the header row; the
  // card's back button takes the logo's slot.
  const logoHidden = rowOpen && menuView === "shop";
  const logoMorph = {
    duration: reducedMotion ? 0 : rowOpen ? 0.2 : 0.15,
    ease: [...MotionTransition.navMorph.ease] as [
      number,
      number,
      number,
      number,
    ],
  };
  // Keeps the card under the trigger it opened from through its close fade,
  // after menuSurface has already been cleared.
  const cardSurface = menuSurface ?? lastSurfaceRef.current;

  return (
    <div>
      {/* z-40 keeps the open menu — cluster, card and click-catcher — over the
          page through its close fade; the card re-scopes itself to the light
          palette, and the nav follows so its controls stay legible over the
          white card on dark pages. */}
      <nav
        data-theme={menuSurface !== null ? "light" : topNavTheme}
        ref={navRef}
        className="relative z-40 flex items-center justify-between px-grid-margin py-l"
      >
        <motion.div
          aria-hidden={logoHidden}
          inert={logoHidden}
          initial={false}
          animate={{ x: rowOpen ? 20 : 0, opacity: logoHidden ? 0 : 1 }}
          transition={logoMorph}
          className={cn(
            "relative z-20 flex",
            logoHidden && "pointer-events-none",
          )}
        >
          <Link
            href="/"
            aria-label="Matic home"
            className={cn("text-content-primary", CHROME_THEME_FADE_CLASSES)}
          >
            <MaticLogo classes={NAV_WORDMARK_CLASSES} />
          </Link>
        </motion.div>
        <div
          aria-hidden={collapsed}
          inert={collapsed}
          style={chromeFadeStyle}
          className={cn(
            "relative z-20 transition-opacity motion-reduce:transition-none sd:hidden",
            collapsed && "pointer-events-none opacity-0",
          )}
        >
          <MobileNavCluster
            variant="row"
            shopLabel={SHOP_LABEL}
            // Like the pill's, the row's Shop never navigates — it opens the
            // sheet menu straight into the shop sub-view.
            shopAction={{
              kind: "button",
              onClick: () =>
                rowOpen ? openShopView() : openMenu("row", "shop"),
            }}
            shopButtonRef={rowShopButtonRef}
            cartCount={cartCount}
            onCartClick={handleCartClick}
            onMenuClick={() =>
              rowOpen ? closeMenu("close_button") : openMenu("row")
            }
            menuOpen={rowOpen}
            menuButtonRef={rowMenuButtonRef}
            menuControlsId={MENU_CARD_ID}
            shopHidden={rowOpen && menuView === "shop"}
          />
        </div>
        <div
          aria-hidden={collapsed}
          inert={collapsed}
          style={chromeFadeStyle}
          className={cn(
            "relative z-30 hidden transition-opacity motion-reduce:transition-none sd:block",
            collapsed && "pointer-events-none opacity-0",
          )}
        >
          <DesktopNav
            cartCount={cartCount}
            onCartClick={() => openSideCart()}
            collapsed={collapsed}
          />
        </div>
        {menuSurface !== null && (
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => closeMenu("scrim", true)}
            className="fixed inset-0 z-0 cursor-default"
          />
        )}
        <NavMenuCard
          id={MENU_CARD_ID}
          open={menuSurface !== null}
          view={menuView}
          showWordmark={cardSurface === "pill"}
          backButtonRef={backButtonRef}
          onBack={() => setMenuView("menu")}
          onNavigate={() => closeMenu("close_button")}
          className={cn(
            "left-grid-margin right-grid-margin z-10",
            cardSurface === "pill" ? "fixed top-1" : "absolute top-1",
          )}
        />
      </nav>

      {/* Two layers on purpose. The outer one owns the discrete hide — the
          row's own menu is open, so the pill must yield at once — while the
          inner one is driven continuously by scroll position and must not have
          that fight it. Anchored to the same right edge as the row's Shop
          button, so the button it replaces lands in place rather than
          reappearing somewhere else. */}
      <div
        aria-hidden={!floatingVisible}
        inert={!floatingVisible}
        style={chromeFadeStyle}
        className={cn(
          "fixed right-grid-margin top-l z-40 transition-opacity motion-reduce:transition-none sd:top-xl",
          scrollsAway && "hidden",
          (rowOpen || overHidden) && "opacity-0",
          !floatingVisible && "pointer-events-none",
        )}
      >
        <motion.div
          ref={floatingNavRef}
          onPointerMove={handleFloatingPointerMove}
          onPointerLeave={handleFloatingPointerLeave}
          style={{ opacity: handoffOpacity, y: handoffLift }}
        >
          <div className="hidden sd:block">
            <FloatingNavSurface
              expanded={expanded}
              cartCount={cartCount}
              onCartClick={() => openSideCart()}
              onMenuClick={handleFloatingSurfaceMenuClick}
              menuButtonRef={desktopFloatingMenuButtonRef}
              onNavigate={() => setExpanded(false)}
              onDismiss={() => setExpanded(false)}
              onEscape={() => {
                track("nav_dismissed", { method: "escape" });
                collapseFloatingNav();
              }}
            />
          </div>
          <div className="sd:hidden">
            <NavPill
              shopHref={SHOP_HREF}
              shopLabel={SHOP_LABEL}
              cartCount={cartCount}
              onCartClick={handleCartClick}
              onMenuClick={() =>
                pillOpen ? closeMenu("close_button") : openMenu("pill")
              }
              menuButtonRef={mobileFloatingMenuButtonRef}
              menuExpanded={pillOpen || expanded}
              // The pill's Shop never navigates — it opens the sheet menu
              // straight into the shop sub-view.
              shopAction={{
                kind: "button",
                onClick: () =>
                  pillOpen ? openShopView() : openMenu("pill", "shop"),
              }}
              shopButtonRef={pillShopButtonRef}
              shopHidden={pillOpen && menuView === "shop"}
              menuControlsId={MENU_CARD_ID}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SiteNav;
